import { useEffect, useState, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { api, formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  LogOut, Upload, Loader2, ArrowLeft, Trash2, FileText, Sheet,
  ChevronRight, ChevronDown, Layers, Users, Tags, Plus, Save, CheckCircle2, Circle, X, MessageSquareText, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const money = (n) =>
  Number(n || 0).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

async function logoDataUrl() {
  try {
    const res = await fetch("/logo-tecnomyl.png");
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtFecha = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

function ComentarioPanel({ clienteName, value, saving, onSave, onClose }) {
  const [obs, setObs] = useState(value?.observacion || "");
  const [pagos, setPagos] = useState(() => (value?.pagos || []).map((p) => ({ ...p })));

  useEffect(() => {
    setObs(value?.observacion || "");
    setPagos((value?.pagos || []).map((p) => ({ ...p })));
  }, [value]);

  const addPago = () => setPagos((ps) => [...ps, { id: `tmp-${Date.now()}`, fecha: todayISO(), monto: "", cumplido: false }]);
  const updPago = (id, patch) => setPagos((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const delPago = (id) => setPagos((ps) => ps.filter((p) => p.id !== id));

  return (
    <div className="mt-2 ml-5 rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-3" data-testid="cartera-comentario-panel">
      <div>
        <label className="text-[11px] font-semibold text-slate-500">Observación</label>
        <textarea
          value={obs}
          onChange={(e) => setObs(e.target.value)}
          placeholder="Anotá el estado de gestión, acuerdos, notas del cliente…"
          rows={2}
          className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-emerald-400"
          data-testid="cartera-comentario-obs"
        />
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-[11px] font-semibold text-slate-500">Fechas de posible pago</label>
          <Button size="sm" variant="ghost" onClick={addPago} className="h-7 gap-1 text-emerald-700 hover:bg-emerald-100" data-testid="cartera-comentario-add-fecha">
            <Plus className="h-3.5 w-3.5" /> Agregar fecha
          </Button>
        </div>
        {pagos.length === 0 && <p className="text-xs text-slate-400">Sin fechas cargadas.</p>}
        {pagos.map((p) => (
          <div key={p.id} className="flex items-center gap-2 flex-wrap" data-testid="cartera-comentario-fecha-row">
            <Input
              type="date"
              value={p.fecha || ""}
              onChange={(e) => updPago(p.id, { fecha: e.target.value })}
              className={`h-8 w-40 ${p.cumplido ? "line-through text-slate-400" : ""}`}
              data-testid="cartera-comentario-fecha-input"
            />
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">U$D</span>
              <Input
                type="text"
                inputMode="decimal"
                value={p.monto ?? ""}
                onChange={(e) => updPago(p.id, { monto: e.target.value })}
                placeholder="0,00"
                className="h-8 w-32 pl-9 text-right"
                data-testid="cartera-comentario-monto-input"
              />
            </div>
            <button
              onClick={() => updPago(p.id, { cumplido: !p.cumplido })}
              className={`inline-flex items-center gap-1 text-xs font-medium rounded-md px-2 py-1 transition-colors ${p.cumplido ? "bg-emerald-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-emerald-50"}`}
              data-testid="cartera-comentario-cumplido-toggle"
            >
              {p.cumplido ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
              {p.cumplido ? "Cumplido" : "Pendiente"}
            </button>
            <button onClick={() => delPago(p.id)} className="text-red-500 hover:text-red-700 p-1" data-testid="cartera-comentario-del-fecha">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button size="sm" variant="ghost" onClick={onClose} className="h-8" data-testid="cartera-comentario-cancel">Cerrar</Button>
        <Button size="sm" onClick={() => onSave(clienteName, { observacion: obs, pagos })} disabled={saving}
          className="h-8 gap-1.5 bg-emerald-700 hover:bg-emerald-800" data-testid="cartera-comentario-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Guardar
        </Button>
      </div>
    </div>
  );
}

const Marca = ({ on }) => (
  <span className={`h-4 w-4 shrink-0 rounded-sm border flex items-center justify-center ${on ? "bg-emerald-700 border-emerald-700 text-white" : "border-slate-400"}`}>
    {on && <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden="true"><path d="M2 6.5l2.5 2.5L10 3.5" fill="none" stroke="currentColor" strokeWidth="2" /></svg>}
  </span>
);

function TiposVenta({ tipos, saldos, sel, setSel }) {
  const [abierto, setAbierto] = useState(false);
  const marcado = (t) => sel.includes(t);
  const cambiar = (t) => setSel(marcado(t) ? sel.filter((x) => x !== t) : [...sel, t]);
  const sumaSel = sel.reduce((a, t) => a + (saldos[t] || 0), 0);
  const etiqueta = !sel.length ? "Todos los tipos" : sel.length === 1 ? sel[0] : `${sel.length} tipos seleccionados`;
  return (
    <Popover open={abierto} onOpenChange={setAbierto}>
      <PopoverTrigger asChild>
        <button type="button" className="h-9 w-56 rounded-md border border-input bg-background px-3 text-sm text-left flex items-center justify-between gap-2" data-testid="cartera-filter-tipo">
          <span className="truncate">{etiqueta}</span><ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[26rem] p-0">
        <div className="max-h-80 overflow-y-auto py-1">
          <button type="button" onClick={() => setSel([])} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50" data-testid="cartera-tipo-todos">
            <Marca on={!sel.length} /> <span className="font-medium">Todos los tipos</span>
          </button>
          {tipos.map((t) => (
            <button type="button" key={t} onClick={() => cambiar(t)} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50" data-testid={`cartera-tipo-${t}`}>
              <Marca on={marcado(t)} /> <span className="truncate text-left">{t}</span>
              <span className="ml-auto tabular-nums text-xs text-slate-500">{money(saldos[t])}</span>
            </button>
          ))}
        </div>
        <div className="border-t px-3 py-2 flex items-center justify-between text-sm bg-slate-50">
          <span className="text-slate-600">{sel.length ? `Seleccionado (${sel.length})` : "Elegí uno o varios"}</span>
          <span className="flex items-center gap-3">
            {!!sel.length && <b className="tabular-nums text-emerald-800" data-testid="cartera-tipo-suma">{money(sumaSel)}</b>}
            <button type="button" onClick={() => setAbierto(false)} className="text-emerald-700 font-semibold text-xs">Listo</button>
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function CarteraSection({ user, onLogout, onBack }) {
  const fileRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(() => new Set());
  const [fechaCorte, setFechaCorte] = useState("");
  const [showVend, setShowVend] = useState(false);
  const [showTipos, setShowTipos] = useState(false);
  // filtros
  const [vendedor, setVendedor] = useState("__all__");
  const [tiposSel, setTiposSel] = useState([]);   // varios tipos de venta a la vez (vacío = todos)
  const [cliente, setCliente] = useState("");
  const [montoMin, setMontoMin] = useState("");
  // comentarios por cliente
  const [comentarios, setComentarios] = useState({});
  const [openComments, setOpenComments] = useState(() => new Set());
  const [savingComment, setSavingComment] = useState(null);
  // exportación con opciones
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFmt, setExportFmt] = useState("pdf");
  const [optVendedor, setOptVendedor] = useState(true);
  const [optComentarios, setOptComentarios] = useState(false);
  const [optResumenIA, setOptResumenIA] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadComentarios = useCallback(async () => {
    try {
      const res = await api.get("/cartera/comentarios");
      setComentarios(res.data || {});
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadComentarios(); }, [loadComentarios]);

  const nrm = (s) => (s || "").toString().trim().toUpperCase().replace(/\s+/g, " ");
  const toggleComment = (key) =>
    setOpenComments((s) => {
      const n = new Set(s);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  const saveComentario = async (cliente, draft) => {
    setSavingComment(nrm(cliente));
    const tid = toast.loading("Guardando comentario…");
    try {
      const res = await api.put("/cartera/comentarios", { cliente, ...draft });
      const key = res.data?.key;
      setComentarios((c) => {
        const next = { ...c };
        if (res.data?.deleted) { for (const k of Object.keys(next)) if (nrm(next[k]?.cliente) === nrm(cliente)) delete next[k]; }
        else if (key) next[key] = { cliente, observacion: res.data.observacion, pagos: res.data.pagos };
        return next;
      });
      toast.success("Comentario guardado", { id: tid });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar", { id: tid });
    } finally {
      setSavingComment(null);
    }
  };

  const comentarioDe = (c) => comentarios[c.key] || null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (vendedor !== "__all__") params.vendedor = vendedor;
      if (tiposSel.length) params.tipos = tiposSel.join("|");
      if (cliente.trim()) params.cliente = cliente.trim();
      if (montoMin !== "" && !isNaN(Number(montoMin))) params.monto_min = Number(montoMin);
      const res = await api.get("/cartera", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al cargar la cartera");
    } finally {
      setLoading(false);
    }
  }, [vendedor, tiposSel, cliente, montoMin]);

  useEffect(() => {
    const t = setTimeout(load, 300); // debounce filtros de texto
    return () => clearTimeout(t);
  }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    if (fileRef.current) fileRef.current.value = "";
    const tid = toast.loading("Procesando la planilla…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("fecha_corte", fechaCorte || "");
      const res = await api.post("/cartera/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(res.data.message || "Planilla importada", { id: tid });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo importar la planilla", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Borrar todos los datos cargados de la cartera?")) return;
    setBusy(true);
    try {
      await api.delete("/cartera");
      toast.success("Datos borrados");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo borrar");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (fv) =>
    setOpen((s) => {
      const n = new Set(s);
      n.has(fv) ? n.delete(fv) : n.add(fv);
      return n;
    });

  const grupos = data?.grupos || [];
  const allOpen = grupos.length > 0 && grupos.every((g) => open.has(g.vencimiento));
  const toggleAll = () =>
    setOpen(allOpen ? new Set() : new Set(grupos.map((g) => g.vencimiento)));

  const commentText = (com) => {
    if (!com) return "";
    const parts = [];
    if ((com.observacion || "").trim()) parts.push(`Obs.: ${com.observacion.trim()}`);
    if (com.pagos?.length)
      parts.push("Posible pago: " + com.pagos.map((p) => `${fmtFecha(p.fecha)}${p.monto ? " U$D " + p.monto : ""} (${p.cumplido ? "Cumplido" : "Pendiente"})`).join(", "));
    return parts.join("   ·   ");
  };

  const buildStats = () => {
    const porVend = {};
    const clientes = [];
    grupos.forEach((g) =>
      g.clientes.forEach((c) => {
        porVend[c.vendedor] = (porVend[c.vendedor] || 0) + (c.saldo || 0);
        clientes.push({ cliente: c.cliente, vendedor: c.vendedor, saldo: Math.round(c.saldo || 0) });
      }));
    clientes.sort((a, b) => b.saldo - a.saldo);
    const por_vendedor = Object.entries(porVend)
      .map(([vendedor, saldo]) => ({ vendedor, saldo: Math.round(saldo) }))
      .sort((a, b) => b.saldo - a.saldo).slice(0, 10);
    return {
      fecha_corte: data?.fecha_corte || null,
      total: Math.round(data?.total || 0),
      n_clientes: clientes.length,
      n_vencimientos: grupos.length,
      top_clientes: clientes.slice(0, 8),
      por_vendedor,
    };
  };

  const exportExcel = (opts) => {
    const incV = opts.vendedor, incC = opts.comentarios;
    const header = ["Vencimiento", "Cliente"];
    if (incV) header.push("Vendedor");
    header.push("Saldo Corregido (US$)");
    if (incC) header.push("Observación", "Fechas de posible pago");
    const aoa = [];
    if (opts.resumen) {
      aoa.push(["RESUMEN EJECUTIVO"]);
      opts.resumen.split("\n").forEach((l) => { if (l.trim()) aoa.push([l.trim()]); });
      aoa.push([]);
    }
    aoa.push(header);
    grupos.forEach((g) => {
      g.clientes.forEach((c) => {
        const row = [g.vencimiento_fmt, c.cliente];
        if (incV) row.push(c.vendedor);
        row.push(c.saldo);
        if (incC) {
          const com = comentarios[c.key];
          row.push(com?.observacion || "");
          row.push((com?.pagos || []).map((p) => `${fmtFecha(p.fecha)}${p.monto ? " U$D " + p.monto : ""} (${p.cumplido ? "Cumplido" : "Pendiente"})`).join(" | "));
        }
        aoa.push(row);
      });
      const subRow = [`Subtotal ${g.vencimiento_fmt}`, ""];
      if (incV) subRow.push("");
      subRow.push(g.subtotal);
      aoa.push(subRow);
    });
    const totalRow = ["TOTAL GENERAL", ""];
    if (incV) totalRow.push("");
    totalRow.push(data?.total || 0);
    aoa.push(totalRow);
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const cols = [{ wch: 16 }, { wch: 48 }];
    if (incV) cols.push({ wch: 32 });
    cols.push({ wch: 20 });
    if (incC) cols.push({ wch: 46 }, { wch: 44 });
    ws["!cols"] = cols;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cartera Vencimientos");
    XLSX.writeFile(wb, `Cartera_Vencimientos${data?.fecha_corte ? "_" + data.fecha_corte : ""}.xlsx`);
  };

  const exportPDF = async (opts) => {
    const incV = opts.vendedor, incC = opts.comentarios;
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    doc.setFillColor(15, 82, 51);
    doc.rect(0, 0, W, 70, "F");
    const logo = await logoDataUrl();
    if (logo) { try { doc.addImage(logo, "PNG", 40, 16, 90, 38); } catch { /* ignore */ } }
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Cartera · Deudas por Vencimiento", W - 40, 34, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Tecnomyl S.A.${data?.fecha_corte ? " — Corte: " + data.fecha_corte : ""}`, W - 40, 52, { align: "right" });

    let startY = 88;
    if (opts.resumen) {
      doc.setFillColor(240, 246, 243);
      doc.setDrawColor(200, 224, 210);
      doc.setTextColor(15, 82, 51);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      const lines = doc.splitTextToSize(opts.resumen.replace(/\n{2,}/g, "\n"), W - 100);
      const boxH = 24 + lines.length * 11 + 12;
      doc.roundedRect(40, startY, W - 80, boxH, 5, 5, "FD");
      doc.text("Resumen Ejecutivo (IA)", 52, startY + 17);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(60, 60, 60);
      doc.text(lines, 52, startY + 32);
      startY += boxH + 14;
    }

    const head = incV
      ? ["Vencimiento", "Cliente", "Vendedor", "Saldo Corregido"]
      : ["Vencimiento", "Cliente", "Saldo Corregido"];
    const ncol = incV ? 4 : 3;
    const grpStyle = { fontStyle: "bold", fillColor: [226, 240, 233], textColor: [15, 82, 51] };
    const body = [];
    grupos.forEach((g) => {
      const grp = { content: g.vencimiento_fmt, styles: { ...grpStyle } };
      const sub = { content: money(g.subtotal), styles: { ...grpStyle, halign: "right" } };
      body.push(incV ? [grp, "", "", sub] : [grp, "", sub]);
      g.clientes.forEach((c) => {
        body.push(incV
          ? ["", c.cliente, c.vendedor, { content: money(c.saldo), styles: { halign: "right" } }]
          : ["", c.cliente, { content: money(c.saldo), styles: { halign: "right" } }]);
        if (incC) {
          const txt = commentText(comentarios[c.key]);
          if (txt)
            body.push(["", { content: txt, colSpan: ncol - 1,
              styles: { fontStyle: "italic", textColor: [90, 90, 90], fontSize: 7, fillColor: [249, 250, 249] } }]);
        }
      });
    });
    const columnStyles = incV
      ? { 0: { cellWidth: 76 }, 3: { halign: "right", cellWidth: 86 } }
      : { 0: { cellWidth: 76 }, 2: { halign: "right", cellWidth: 100 } };
    autoTable(doc, {
      startY,
      head: [head],
      body,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [15, 82, 51], halign: "left", textColor: [255, 255, 255] },
      columnStyles,
      margin: { left: 40, right: 40 },
      foot: [[{ content: "TOTAL GENERAL", colSpan: ncol - 1, styles: { fontStyle: "bold" } },
        { content: money(data?.total || 0), styles: { fontStyle: "bold", halign: "right" } }]],
      footStyles: { fillColor: [15, 82, 51], textColor: [255, 255, 255] },
    });
    doc.save(`Cartera_Vencimientos${data?.fecha_corte ? "_" + data.fecha_corte : ""}.pdf`);
  };

  const openExport = (fmt) => { setExportFmt(fmt); setExportOpen(true); };

  const doExport = async () => {
    setGenerating(true);
    try {
      let resumen = "";
      if (optResumenIA) {
        const tid = toast.loading("Generando resumen con IA…");
        try {
          const res = await api.post("/cartera/resumen-ia", buildStats());
          resumen = res.data?.resumen || "";
          toast.success("Resumen generado", { id: tid });
        } catch (err) {
          toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo generar el resumen IA", { id: tid });
        }
      }
      const opts = { vendedor: optVendedor, comentarios: optComentarios, resumen };
      if (exportFmt === "pdf") await exportPDF(opts);
      else exportExcel(opts);
      setExportOpen(false);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f7f5]" data-testid="cartera-section">
      <header className="bg-[#0f5233] text-white sticky top-0 z-40 shadow">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg px-2 py-1"><Logo /></div>
            <div className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              <span className="font-semibold text-lg">Cartera · Vencimientos</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:block text-xs text-emerald-100">{user?.email}</span>
            {onBack && (
              <Button size="sm" variant="secondary" onClick={onBack} data-testid="cartera-back-btn" className="gap-2">
                <ArrowLeft className="h-4 w-4" /> Volver
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={onLogout} data-testid="cartera-logout-btn" className="gap-2">
              <LogOut className="h-4 w-4" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        {/* Toolbar */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-slate-500">Fecha de corte (opcional)</label>
            <Input type="date" value={fechaCorte} onChange={(e) => setFechaCorte(e.target.value)}
              className="h-9 w-40" data-testid="cartera-fecha-corte" />
          </div>
          <input ref={fileRef} type="file" accept=".xls,.xlsx,.csv" className="hidden"
            onChange={handleUpload} data-testid="cartera-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={busy}
            data-testid="cartera-upload-btn" className="h-9 gap-2 bg-emerald-700 hover:bg-emerald-800">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Cargar Excel
          </Button>
          <Button onClick={() => openExport("excel")} disabled={!grupos.length} variant="outline"
            data-testid="cartera-export-excel" className="h-9 gap-2">
            <Sheet className="h-4 w-4 text-emerald-600" /> Excel
          </Button>
          <Button onClick={() => openExport("pdf")} disabled={!grupos.length} variant="outline"
            data-testid="cartera-export-pdf" className="h-9 gap-2">
            <FileText className="h-4 w-4 text-red-600" /> PDF
          </Button>
          <Button onClick={handleDelete} disabled={busy || !grupos.length} variant="outline"
            data-testid="cartera-delete-btn" className="h-9 gap-2 text-red-600 border-red-200 hover:bg-red-50">
            <Trash2 className="h-4 w-4" /> Borrar
          </Button>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-slate-500">Vendedor</label>
            <Select value={vendedor} onValueChange={setVendedor}>
              <SelectTrigger className="h-9 w-56" data-testid="cartera-filter-vendedor"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los vendedores</SelectItem>
                {(data?.vendedores || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-slate-500">Tipo de venta</label>
            <TiposVenta tipos={data?.tipos || []} saldos={data?.saldo_tipos || {}} sel={tiposSel} setSel={setTiposSel} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-slate-500">Cliente</label>
            <Input value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder="Buscar cliente…"
              className="h-9 w-56" data-testid="cartera-filter-cliente" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-slate-500">Saldo vencido mayor a (US$)</label>
            <Input type="number" value={montoMin} onChange={(e) => setMontoMin(e.target.value)} placeholder="0"
              className="h-9 w-44" data-testid="cartera-filter-monto" />
          </div>
          {grupos.length > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowVend((v) => !v)}
                className={`h-9 gap-1.5 ${showVend ? "bg-emerald-700 text-white hover:bg-emerald-800 border-emerald-700" : ""}`}
                data-testid="cartera-toggle-vend">
                <Users className="h-4 w-4" /> {showVend ? "Ocultar vendedores" : "Mostrar vendedores"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowTipos((v) => !v)}
                className={`h-9 gap-1.5 ${showTipos ? "bg-emerald-700 text-white hover:bg-emerald-800 border-emerald-700" : ""}`}
                data-testid="cartera-toggle-tipos">
                <Tags className="h-4 w-4" /> {showTipos ? "Ocultar tipo de venta" : "Tipo de venta"}
              </Button>
              <Button variant="ghost" size="sm" onClick={toggleAll} className="h-9 text-emerald-700"
                data-testid="cartera-toggle-all">
                {allOpen ? "Colapsar todo" : "Expandir todo"}
              </Button>
            </div>
          )}
        </div>

        {/* Resumen */}
        {data && grupos.length > 0 && (
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="rounded-full bg-emerald-50 text-emerald-800 px-3 py-1 font-semibold" data-testid="cartera-total">
              {tiposSel.length ? `Total de ${tiposSel.length === 1 ? "lo seleccionado" : `los ${tiposSel.length} tipos seleccionados`}` : "Total"}: {money(data.total)}
            </span>
            <span className="rounded-full bg-slate-100 text-slate-700 px-3 py-1">{data.vencimientos} vencimientos</span>
            <span className="rounded-full bg-slate-100 text-slate-700 px-3 py-1">{data.clientes} líneas de cliente</span>
          </div>
        )}
        {data && grupos.length > 0 && tiposSel.length > 1 && (
          <div className="flex flex-wrap gap-2 text-xs" data-testid="cartera-total-por-tipo">
            {(data.total_por_tipo || []).map((t) => (
              <span key={t.tipo} className="rounded-md border border-emerald-200 bg-white px-2.5 py-1 text-slate-700">
                <span className="uppercase text-slate-500">{t.tipo}</span> <b className="tabular-nums text-slate-800">{money(t.saldo)}</b>
              </span>
            ))}
          </div>
        )}

        {/* Tabla agrupada */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-emerald-700" /></div>
        ) : !grupos.length ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-500"
            data-testid="cartera-empty">
            No hay datos cargados. Subí el <b>Informe de Cuentas a Recibir</b> en Excel para generar el informe.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="cartera-table">
            {grupos.map((g) => {
              const isOpen = open.has(g.vencimiento);
              return (
                <div key={g.vencimiento} className="border-b border-slate-100 last:border-0">
                  <button onClick={() => toggle(g.vencimiento)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-[#c7e2d2] hover:bg-[#b6d9c2] border-t border-[#9ccbb0] transition-colors"
                    data-testid={`cartera-group-${g.vencimiento}`}>
                    <span className="flex items-center gap-2 font-bold text-[#0b4028]">
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {g.vencimiento_fmt}
                      <span className="text-xs font-normal text-emerald-800/70">({g.clientes.length} cliente{g.clientes.length !== 1 ? "s" : ""})</span>
                    </span>
                    <span className="font-bold text-[#0b4028] tabular-nums">{money(g.subtotal)}</span>
                  </button>
                  {isOpen && (
                    <div className="divide-y divide-slate-200" data-testid={`cartera-group-body-${g.vencimiento}`}>
                      {g.clientes.map((c, i) => {
                        const com = comentarioDe(c);
                        const isCommentOpen = openComments.has(c.key);
                        const hasInfo = !!(com && ((com.observacion || "").trim() || (com.pagos || []).length));
                        return (
                        <div key={i} className="px-4 py-2.5" data-testid="cartera-client-row">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className={`truncate ${showTipos ? "font-semibold text-slate-900" : "text-slate-800"}`}>{c.cliente}</span>
                              {showVend && <span className="text-xs text-slate-500 shrink-0" data-testid="cartera-client-vendedor">{c.vendedor}</span>}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="font-medium tabular-nums text-slate-800">{money(c.saldo)}</span>
                              <button
                                onClick={() => toggleComment(c.key)}
                                title={hasInfo ? "Ver/editar comentarios" : "Agregar comentario"}
                                data-testid="cartera-client-add-comment"
                                className={`inline-flex items-center justify-center h-7 w-7 rounded-md border transition-colors ${hasInfo ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-slate-200 text-slate-400 hover:bg-slate-50 hover:text-emerald-700"}`}>
                                {isCommentOpen ? <X className="h-4 w-4" /> : hasInfo ? <MessageSquareText className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                          {isCommentOpen && (
                            <ComentarioPanel
                              clienteName={c.cliente}
                              value={com}
                              saving={savingComment === nrm(c.cliente)}
                              onSave={saveComentario}
                              onClose={() => toggleComment(c.key)}
                            />
                          )}
                          {showTipos && (
                            <div className="mt-1.5 ml-5 border-l-2 border-emerald-200 pl-3 space-y-1" data-testid="cartera-client-tipos">
                              {(c.tipos || []).map((t, j) => (
                                <div key={j} className="flex items-center justify-between text-[13px] text-slate-600">
                                  <span className="uppercase">{t.tipo}</span>
                                  <span className="tabular-nums pl-3">{money(t.saldo)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );})}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="flex items-center justify-between px-4 py-3 bg-[#0f5233] text-white font-bold">
              <span>TOTAL GENERAL</span>
              <span className="tabular-nums">{money(data.total)}</span>
            </div>
          </div>
        )}
      </main>

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="sm:max-w-md" data-testid="cartera-export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {exportFmt === "pdf" ? <FileText className="h-5 w-5 text-red-600" /> : <Sheet className="h-5 w-5 text-emerald-600" />}
              Opciones de exportación {exportFmt === "pdf" ? "PDF" : "Excel"}
            </DialogTitle>
            <DialogDescription>Elegí qué información incluir en el informe.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer hover:bg-slate-50" data-testid="cartera-opt-vendedor">
              <Checkbox checked={optVendedor} onCheckedChange={(v) => setOptVendedor(!!v)} className="mt-0.5" />
              <div>
                <p className="text-sm font-medium text-slate-800">Detalle de vendedores</p>
                <p className="text-xs text-slate-500">Incluye la columna Vendedor en el informe.</p>
              </div>
            </label>
            <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer hover:bg-slate-50" data-testid="cartera-opt-comentarios">
              <Checkbox checked={optComentarios} onCheckedChange={(v) => setOptComentarios(!!v)} className="mt-0.5" />
              <div>
                <p className="text-sm font-medium text-slate-800">Comentarios cargados</p>
                <p className="text-xs text-slate-500">Observaciones y fechas de posible pago de cada cliente.</p>
              </div>
            </label>
            <label className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 cursor-pointer hover:bg-emerald-50" data-testid="cartera-opt-resumen-ia">
              <Checkbox checked={optResumenIA} onCheckedChange={(v) => setOptResumenIA(!!v)} className="mt-0.5" />
              <div>
                <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                  <Sparkles className="h-4 w-4 text-emerald-600" /> Resumen ejecutivo con IA
                </p>
                <p className="text-xs text-slate-500">Genera un análisis profesional de la cartera con Gemini.</p>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExportOpen(false)} disabled={generating} data-testid="cartera-export-cancel">Cancelar</Button>
            <Button onClick={doExport} disabled={generating} className="gap-2 bg-emerald-700 hover:bg-emerald-800" data-testid="cartera-export-generate">
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : (exportFmt === "pdf" ? <FileText className="h-4 w-4" /> : <Sheet className="h-4 w-4" />)}
              Generar {exportFmt === "pdf" ? "PDF" : "Excel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
